import { forwardRef, type HTMLAttributes } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn.ts'

const trackVariants = cva('w-full overflow-hidden rounded-full bg-border/60', {
  variants: {
    size: {
      sm: 'h-1',
      md: 'h-1.5',
      lg: 'h-2.5',
    },
  },
  defaultVariants: { size: 'md' },
})

const fillToneClass = {
  primary: 'bg-primary',
  success: 'bg-toast-success-text',
  warning: 'bg-toast-warning-text',
  danger: 'bg-toast-error-text',
} as const

export type ProgressTone = keyof typeof fillToneClass

export interface ProgressBarProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'role' | 'children'>,
    VariantProps<typeof trackVariants> {
  /** Percent value (0–100). Values outside the range are clamped. */
  value: number
  /** Visual tone of the filled portion. Default `primary`. */
  tone?: ProgressTone
  /** Used as `aria-label`. */
  label?: string
}

export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(function ProgressBar(
  { className, value, tone = 'primary', size, label, ...props },
  ref,
) {
  const pct = Math.max(0, Math.min(100, value))
  return (
    <div
      ref={ref}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={pct}
      aria-label={label}
      className={cn(trackVariants({ size }), className)}
      {...props}
    >
      <div
        className={cn('h-full rounded-full transition-[width]', fillToneClass[tone])}
        style={{ width: `${pct}%` }}
      />
    </div>
  )
})
